/**
 * AICORES API client (web-ui).
 *
 * Зеркало контракта `/api/v1/chat/stream`, `/api/auth/*`, `/api/chats`,
 * `/api/v1/vps/*`, `/api/v1/tools/gaps` (новый эндпоинт этапа 1.3).
 *
 * Унаследован стиль из `apps/aicores-desktop/src/lib/jarvisApi.ts`,
 * но без Tauri-специфичных частей и с явной типизацией событий стрима.
 */

import { getBaseUrl, getToken } from "./storage";
import type {
  ChatStreamEvent,
  LoginResult,
  RemoteChatDetail,
  RemoteChatMessage,
  RemoteChatSummary,
  UserMe,
} from "./types";

// === Helpers ===

function authHeaders(): HeadersInit {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** SSO cookie aicores_token + Bearer — как в dashboard.html */
function apiFetchInit(extra: RequestInit = {}): RequestInit {
  return {
    credentials: "include",
    ...extra,
    headers: { ...authHeaders(), ...(extra.headers as Record<string, string> | undefined) },
  };
}

function parseEpoch(raw: unknown): number {
  if (typeof raw === "number") {
    return raw < 10_000_000_000 ? raw * 1000 : raw;
  }
  if (typeof raw === "string") {
    const v = Number(raw);
    if (Number.isFinite(v)) return v < 10_000_000_000 ? v * 1000 : v;
  }
  return Date.now();
}

async function readJsonOrThrow(response: Response, fallbackMessage: string): Promise<unknown> {
  const text = await response.text();
  const ct = (response.headers.get("content-type") || "").toLowerCase();
  if (!response.ok) throw new Error(text || fallbackMessage);
  if (ct.includes("text/html") || text.trimStart().startsWith("<")) {
    throw new Error("Сервер вернул HTML вместо JSON. Проверьте URL API.");
  }
  return JSON.parse(text) as unknown;
}

// === Auth ===

export async function register(email: string, username: string, password: string): Promise<LoginResult> {
  const r = await fetch(`${getBaseUrl()}/api/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, username, password }),
  });
  return (await readJsonOrThrow(r, "Регистрация не удалась")) as LoginResult;
}

export async function login(email: string, password: string): Promise<LoginResult> {
  const r = await fetch(`${getBaseUrl()}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  return (await readJsonOrThrow(r, "Вход не удался")) as LoginResult;
}

export async function fetchMe(): Promise<UserMe> {
  const r = await fetch(`${getBaseUrl()}/api/auth/me`, apiFetchInit());
  return (await readJsonOrThrow(r, "Профиль не загружен")) as UserMe;
}

// === Chats ===

export async function fetchChats(): Promise<RemoteChatSummary[]> {
  const r = await fetch(`${getBaseUrl()}/api/chats`, apiFetchInit());
  const raw = (await readJsonOrThrow(r, "Список чатов не загружен")) as
    | Array<Record<string, unknown>>
    | { items?: Array<Record<string, unknown>> };
  const arr = Array.isArray(raw) ? raw : Array.isArray(raw.items) ? raw.items : [];
  return arr
    .map((it) => ({
      id: String(it.id || ""),
      title: String(it.title || "Чат"),
      updatedAt: parseEpoch(it.updated_at),
      pinned: Boolean(it.pinned),
    }))
    .filter((it) => it.id);
}

export async function fetchChat(chatId: string): Promise<RemoteChatDetail> {
  const r = await fetch(
    `${getBaseUrl()}/api/chats/${encodeURIComponent(chatId)}`,
    apiFetchInit(),
  );
  const payload = (await readJsonOrThrow(r, "Не удалось открыть чат")) as Record<string, unknown>;
  const root = (payload.chat as Record<string, unknown> | undefined) || payload;
  const history = (root.history as Record<string, unknown> | undefined) || {};
  const map = (history.messages as Record<string, Record<string, unknown>> | undefined) || {};
  const messages: RemoteChatMessage[] = Object.values(map)
    .map((item) => ({
      id: String(item.id || ""),
      role: String(item.role || "assistant"),
      text: String(item.content || ""),
      createdAt: parseEpoch(item.timestamp),
    }))
    .filter((m) => m.id)
    .sort((a, b) => a.createdAt - b.createdAt);
  return {
    id: String(payload.id || chatId),
    title: String(root.title || payload.title || "Чат"),
    updatedAt: parseEpoch(payload.updated_at),
    pinned: Boolean(payload.pinned),
    messages,
  };
}

export async function deleteRemoteChat(chatId: string): Promise<{ success: boolean }> {
  const r = await fetch(`${getBaseUrl()}/api/chats/${encodeURIComponent(chatId)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!r.ok && r.status !== 404) {
    const t = await r.text();
    throw new Error(`Удаление чата: ${r.status} ${t.slice(0, 200)}`);
  }
  return { success: true };
}

// === VPS ===

export async function vpsStatus(): Promise<unknown> {
  const r = await fetch(`${getBaseUrl()}/api/v1/vps/status`, { headers: authHeaders() });
  return readJsonOrThrow(r, "VPS status недоступен");
}

/** Тариф пользователя (free/start/business/agency). Cookie-aware (SSO). */
export interface BillingStatus {
  plan: string;
  tokens_used: number;
  total_limit: number;
}
export async function billingStatus(): Promise<BillingStatus> {
  const r = await fetch(`${getBaseUrl()}/api/billing/status`, apiFetchInit());
  const raw = (await readJsonOrThrow(r, "Статус тарифа недоступен")) as Record<string, unknown>;
  return {
    plan: String(raw.plan || "free"),
    tokens_used: Number(raw.tokens_used || 0),
    total_limit: Number(raw.total_limit || 0),
  };
}

/** true, если у юзера активный платный тариф (не free). */
export async function isPaidPlan(): Promise<boolean> {
  try {
    const s = await billingStatus();
    return (s.plan || "free").toLowerCase().trim() !== "free";
  } catch {
    return false;
  }
}

export async function vpsConnect(body: {
  host: string;
  user: string;
  password?: string | null;
  port?: number;
}): Promise<unknown> {
  const r = await fetch(`${getBaseUrl()}/api/v1/vps/connect`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return readJsonOrThrow(r, "VPS connect не удался");
}

export async function vpsDisconnect(): Promise<unknown> {
  const r = await fetch(`${getBaseUrl()}/api/v1/vps/disconnect`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: "{}",
  });
  return readJsonOrThrow(r, "VPS disconnect не удался");
}

// === Tools / Gaps (этап 1.3) ===

export interface ToolGapItem {
  tool_name: string;
  kind: string;
  count: number;
  reason?: string;
  severity?: string;
  source?: string;
}

export async function fetchToolGaps(): Promise<{ total: number; missing: number; persistent_error: number; items: ToolGapItem[] }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/tools/gaps`, { headers: authHeaders() });
  return (await readJsonOrThrow(r, "Tool gaps недоступны")) as {
    total: number;
    missing: number;
    persistent_error: number;
    items: ToolGapItem[];
  };
}

// === Workspace file viewer (FileEditor — Пункт 5.x / фидбэк Сессии 7) ===

export interface WorkspaceFile {
  path: string;
  content: string;
  size?: number;
  language?: string;
}

export async function fetchWorkspaceFile(path: string): Promise<WorkspaceFile> {
  const r = await fetch(
    `${getBaseUrl()}/api/v1/aicores-ui/workspace/file?path=${encodeURIComponent(path)}`,
    { headers: authHeaders() },
  );
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`${r.status}: ${t.slice(0, 200)}`);
  }
  const data = (await r.json()) as Record<string, unknown>;
  return {
    path: String(data.path || path),
    content: String(data.content || data.text || ""),
    size: typeof data.size === "number" ? (data.size as number) : undefined,
    language: typeof data.language === "string" ? (data.language as string) : undefined,
  };
}

// === Proposed edits (DiffPanel — Пункт 0.2 / фидбэк Сессии 7) ===

export async function applyProposedEdit(
  edit_id: string,
): Promise<{ success: boolean; path: string }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/workspace/apply`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ edit_id }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`Apply: ${r.status} ${t.slice(0, 200)}`);
  }
  return (await r.json()) as { success: boolean; path: string };
}

export async function rejectProposedEdit(
  edit_id: string,
): Promise<{ success: boolean }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/workspace/reject`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ edit_id }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`Reject: ${r.status} ${t.slice(0, 200)}`);
  }
  return (await r.json()) as { success: boolean };
}

// === DevOps Native (P0): processes / services / containers ====================

export interface NativeProcess {
  pid: number;
  ppid: number;
  user: string;
  cpu_percent: number;
  mem_percent: number;
  uptime_seconds: number;
  rss_kb: number;
  state: string;
  command: string;
}

export async function fetchNativeProcesses(opts: {
  filter?: string;
  sort?: "cpu" | "mem" | "pid" | "etime";
  limit?: number;
} = {}): Promise<{ items: NativeProcess[]; total: number }> {
  const params = new URLSearchParams();
  if (opts.filter) params.set("filter", opts.filter);
  if (opts.sort) params.set("sort", opts.sort);
  if (opts.limit) params.set("limit", String(opts.limit));
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/processes?${params.toString()}`, {
    headers: authHeaders(),
  });
  return (await readJsonOrThrow(r, "Список процессов недоступен")) as {
    items: NativeProcess[];
    total: number;
  };
}

export async function killNativeProcess(
  pid: number,
  opts: { force?: boolean; confirm?: boolean } = {},
): Promise<{ success: boolean; stdout: string; stderr: string }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/process/${pid}/kill`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ force: !!opts.force, confirm: !!opts.confirm }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`kill: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as { success: boolean; stdout: string; stderr: string };
}

export async function reniceNativeProcess(
  pid: number,
  nice: number,
): Promise<{ success: boolean; stdout: string; stderr: string }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/process/${pid}/renice`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ nice }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`renice: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as { success: boolean; stdout: string; stderr: string };
}

export interface NativeService {
  name: string;
  load: string;
  active: string;
  sub: string;
  description: string;
}

export async function fetchNativeServices(opts: {
  state?: "all" | "active" | "running" | "failed" | "inactive";
  filter?: string;
} = {}): Promise<{ items: NativeService[]; total: number }> {
  const params = new URLSearchParams();
  if (opts.state) params.set("state", opts.state);
  if (opts.filter) params.set("filter", opts.filter);
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/services?${params.toString()}`, {
    headers: authHeaders(),
  });
  return (await readJsonOrThrow(r, "Список сервисов недоступен")) as {
    items: NativeService[];
    total: number;
  };
}

export async function nativeServiceAction(
  name: string,
  action: "start" | "stop" | "restart" | "reload" | "enable" | "disable",
  opts: { confirm?: boolean } = {},
): Promise<{ success: boolean; stdout: string; stderr: string }> {
  const r = await fetch(
    `${getBaseUrl()}/api/v1/devops/service/${encodeURIComponent(name)}/${action}`,
    {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ confirm: !!opts.confirm }),
    },
  );
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`service ${action}: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as { success: boolean; stdout: string; stderr: string };
}

export async function fetchNativeServiceLogs(
  name: string,
  n: number = 200,
): Promise<{ success: boolean; logs: string }> {
  const r = await fetch(
    `${getBaseUrl()}/api/v1/devops/service/${encodeURIComponent(name)}/logs?n=${n}`,
    { headers: authHeaders() },
  );
  return (await readJsonOrThrow(r, "Логи сервиса недоступны")) as {
    success: boolean;
    logs: string;
  };
}

export interface NativeContainer {
  id: string;
  name: string;
  image: string;
  status: string;
  state: string;
  running_for: string;
  ports: string;
  command: string;
  cpu_percent: number | null;
  mem_percent: number | null;
  mem_usage: string | null;
}

export async function fetchNativeContainers(opts: {
  all?: boolean;
  filter?: string;
} = {}): Promise<{ items: NativeContainer[]; total: number }> {
  const params = new URLSearchParams();
  params.set("all", String(opts.all ?? true));
  if (opts.filter) params.set("filter", opts.filter);
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/containers?${params.toString()}`, {
    headers: authHeaders(),
  });
  return (await readJsonOrThrow(r, "Список контейнеров недоступен")) as {
    items: NativeContainer[];
    total: number;
  };
}

export async function nativeContainerAction(
  cid: string,
  action: "start" | "stop" | "restart" | "pause" | "unpause" | "rm" | "kill",
  opts: { confirm?: boolean } = {},
): Promise<{ success: boolean; stdout: string; stderr: string }> {
  const r = await fetch(
    `${getBaseUrl()}/api/v1/devops/container/${encodeURIComponent(cid)}/${action}`,
    {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ confirm: !!opts.confirm }),
    },
  );
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`container ${action}: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as { success: boolean; stdout: string; stderr: string };
}

export async function fetchNativeContainerLogs(
  cid: string,
  n: number = 200,
): Promise<{ success: boolean; logs: string }> {
  const r = await fetch(
    `${getBaseUrl()}/api/v1/devops/container/${encodeURIComponent(cid)}/logs?n=${n}`,
    { headers: authHeaders() },
  );
  return (await readJsonOrThrow(r, "Логи контейнера недоступны")) as {
    success: boolean;
    logs: string;
  };
}

// === DevOps Native (P1): files / network / nginx =============================

export interface HostFileEntry {
  name: string;
  type: "dir" | "file" | "link";
  size: number;
  mode: string;
  mtime: number;
  owner_uid: number;
  owner_gid: number;
  link_target: string;
}

export async function fetchHostDir(path: string): Promise<{
  path: string;
  items: HostFileEntry[];
}> {
  const r = await fetch(
    `${getBaseUrl()}/api/v1/devops/files/list?path=${encodeURIComponent(path)}`,
    { headers: authHeaders() },
  );
  return (await readJsonOrThrow(r, "Содержимое директории недоступно")) as {
    path: string;
    items: HostFileEntry[];
  };
}

export async function fetchHostFile(path: string): Promise<{
  path: string;
  size: number;
  content: string;
  is_binary: boolean;
  truncated: boolean;
}> {
  const r = await fetch(
    `${getBaseUrl()}/api/v1/devops/files/read?path=${encodeURIComponent(path)}`,
    { headers: authHeaders() },
  );
  return (await readJsonOrThrow(r, "Файл недоступен")) as {
    path: string;
    size: number;
    content: string;
    is_binary: boolean;
    truncated: boolean;
  };
}

export async function writeHostFile(
  path: string,
  content: string,
  opts: { confirm?: boolean } = {},
): Promise<{ path: string; size: number; created: boolean; backup_path: string }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/files/write`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({
      path,
      content,
      create: true,
      confirm: !!opts.confirm,
    }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`write: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as {
    path: string;
    size: number;
    created: boolean;
    backup_path: string;
  };
}

export async function mkdirHost(path: string, parents = true): Promise<{ path: string; created: boolean }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/files/mkdir`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ path, parents }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`mkdir: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as { path: string; created: boolean };
}

export async function rmHost(
  path: string,
  opts: { recursive?: boolean; confirm?: boolean } = {},
): Promise<{ path: string; removed: boolean }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/files/rm`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({
      path,
      recursive: !!opts.recursive,
      confirm: !!opts.confirm,
    }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`rm: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as { path: string; removed: boolean };
}

// === Network ===================================================================

export interface NativeListenEntry {
  proto: "tcp" | "udp";
  address: string;
  port: number;
  process: string;
}

export async function fetchNetworkListen(): Promise<{ items: NativeListenEntry[]; total: number }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/network/listen`, { headers: authHeaders() });
  return (await readJsonOrThrow(r, "Список портов недоступен")) as {
    items: NativeListenEntry[];
    total: number;
  };
}

export interface NativeConnectionEntry {
  state: string;
  local: string;
  peer: string;
}

export async function fetchNetworkConnections(opts: {
  state?: string;
  limit?: number;
} = {}): Promise<{ items: NativeConnectionEntry[]; total: number; state: string }> {
  const params = new URLSearchParams();
  if (opts.state) params.set("state", opts.state);
  if (opts.limit) params.set("limit", String(opts.limit));
  const r = await fetch(
    `${getBaseUrl()}/api/v1/devops/network/connections?${params.toString()}`,
    { headers: authHeaders() },
  );
  return (await readJsonOrThrow(r, "Соединения недоступны")) as {
    items: NativeConnectionEntry[];
    total: number;
    state: string;
  };
}

export async function fetchFirewall(): Promise<{
  ufw: { success: boolean; output: string; stderr: string };
  iptables: { success: boolean; output: string; stderr: string };
}> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/network/firewall`, { headers: authHeaders() });
  return (await readJsonOrThrow(r, "Firewall недоступен")) as {
    ufw: { success: boolean; output: string; stderr: string };
    iptables: { success: boolean; output: string; stderr: string };
  };
}

// === Nginx =====================================================================

export interface NativeNginxSite {
  name: string;
  enabled: boolean;
  size: number;
  available_path: string;
  enabled_path: string;
}

export async function fetchNginxSites(): Promise<{ items: NativeNginxSite[]; total: number }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/nginx/sites`, { headers: authHeaders() });
  return (await readJsonOrThrow(r, "Список сайтов недоступен")) as {
    items: NativeNginxSite[];
    total: number;
  };
}

export async function nginxTest(): Promise<{ success: boolean; output: string }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/nginx/test`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: "{}",
  });
  return (await readJsonOrThrow(r, "nginx -t")) as { success: boolean; output: string };
}

export async function nginxReload(): Promise<{ success: boolean; output: string }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/nginx/reload`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: "{}",
  });
  return (await readJsonOrThrow(r, "nginx reload")) as { success: boolean; output: string };
}

export async function nginxSiteToggle(
  name: string,
  enable: boolean,
): Promise<{ success: boolean; stdout: string; stderr: string }> {
  const path = enable ? "enable" : "disable";
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/nginx/site/${path}`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`nginx ${path}: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as { success: boolean; stdout: string; stderr: string };
}

export async function nginxCert(opts: {
  domain: string;
  email?: string;
  staging?: boolean;
  confirm?: boolean;
}): Promise<{ success: boolean; output: string }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/nginx/cert`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({
      domain: opts.domain,
      email: opts.email || "",
      staging: !!opts.staging,
      confirm: !!opts.confirm,
    }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`certbot: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as { success: boolean; output: string };
}

export async function fetchNginxLog(
  kind: "access" | "error" = "error",
  n = 200,
): Promise<{ success: boolean; logs: string }> {
  const r = await fetch(
    `${getBaseUrl()}/api/v1/devops/nginx/log?kind=${kind}&n=${n}`,
    { headers: authHeaders() },
  );
  return (await readJsonOrThrow(r, "nginx logs")) as { success: boolean; logs: string };
}

// === DevOps Native (P2): cron / backup / security / packages =================

// --- Cron ---

export async function fetchCronUsers(): Promise<{ users: string[] }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/cron/users`, { headers: authHeaders() });
  return (await readJsonOrThrow(r, "cron users")) as { users: string[] };
}

export async function fetchUserCrontab(user: string): Promise<{
  success: boolean;
  content: string;
  stderr: string;
}> {
  const r = await fetch(
    `${getBaseUrl()}/api/v1/devops/cron/user/${encodeURIComponent(user)}`,
    { headers: authHeaders() },
  );
  return (await readJsonOrThrow(r, "crontab")) as {
    success: boolean;
    content: string;
    stderr: string;
  };
}

export async function writeUserCrontab(
  user: string,
  content: string,
  opts: { confirm?: boolean } = {},
): Promise<{ success: boolean; stdout: string; stderr: string }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/cron/user`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ user, content, confirm: !!opts.confirm }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`crontab: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as { success: boolean; stdout: string; stderr: string };
}

export async function fetchSystemCrons(): Promise<{
  items: Array<{ path: string; kind: string }>;
}> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/cron/system`, { headers: authHeaders() });
  return (await readJsonOrThrow(r, "system cron")) as {
    items: Array<{ path: string; kind: string }>;
  };
}

// --- Backup ---

export interface DockerVolume {
  name: string;
  driver: string;
  mountpoint: string;
}

export interface BackupArchive {
  name: string;
  path: string;
  size: number;
  mtime: number;
}

export async function fetchDockerVolumes(): Promise<{ items: DockerVolume[]; total: number }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/backup/volumes`, { headers: authHeaders() });
  return (await readJsonOrThrow(r, "volumes")) as { items: DockerVolume[]; total: number };
}

export async function fetchBackups(): Promise<{ items: BackupArchive[] }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/backup/list`, { headers: authHeaders() });
  return (await readJsonOrThrow(r, "backups")) as { items: BackupArchive[] };
}

export async function createVolumeBackup(
  volume: string,
): Promise<{ volume: string; name: string; path: string; size: number }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/backup/volume/create`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ volume }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`backup: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as { volume: string; name: string; path: string; size: number };
}

export async function restoreVolumeBackup(
  volume: string,
  backup_name: string,
  opts: { confirm?: boolean } = {},
): Promise<{ volume: string; backup: string; restored: boolean }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/backup/volume/restore`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ volume, backup_name, confirm: !!opts.confirm }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`restore: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as { volume: string; backup: string; restored: boolean };
}

export async function deleteBackup(
  backup_name: string,
  opts: { confirm?: boolean } = {},
): Promise<{ name: string; deleted: boolean }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/backup/delete`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ backup_name, confirm: !!opts.confirm }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`delete backup: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as { name: string; deleted: boolean };
}

export async function pgDump(opts: {
  container: string;
  db?: string;
  user?: string;
}): Promise<{ container: string; db: string; name: string; path: string; size: number }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/backup/pg_dump`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({
      container: opts.container,
      db: opts.db || "",
      user: opts.user || "postgres",
    }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`pg_dump: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as {
    container: string;
    db: string;
    name: string;
    path: string;
    size: number;
  };
}

// --- Security ---

export async function fetchLastLogins(n = 30): Promise<{ success: boolean; output: string }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/security/last?n=${n}`, {
    headers: authHeaders(),
  });
  return (await readJsonOrThrow(r, "last")) as { success: boolean; output: string };
}

export interface SshKeyItem {
  user: string;
  path: string;
  algo: string;
  fingerprint: string;
  comment: string;
}

export async function fetchSshKeys(): Promise<{ items: SshKeyItem[] }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/security/ssh_keys`, {
    headers: authHeaders(),
  });
  return (await readJsonOrThrow(r, "ssh keys")) as { items: SshKeyItem[] };
}

export async function fetchFail2ban(jail = ""): Promise<{
  success: boolean;
  output: string;
  stderr: string;
}> {
  const u = jail ? `?jail=${encodeURIComponent(jail)}` : "";
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/security/fail2ban${u}`, {
    headers: authHeaders(),
  });
  return (await readJsonOrThrow(r, "fail2ban")) as {
    success: boolean;
    output: string;
    stderr: string;
  };
}

export async function fetchSudoHistory(n = 50): Promise<{ success: boolean; output: string }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/security/sudo?n=${n}`, {
    headers: authHeaders(),
  });
  return (await readJsonOrThrow(r, "sudo history")) as { success: boolean; output: string };
}

// --- Packages ---

export async function fetchUpgradablePackages(): Promise<{ success: boolean; output: string }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/packages/upgradable`, {
    headers: authHeaders(),
  });
  return (await readJsonOrThrow(r, "upgradable")) as { success: boolean; output: string };
}

export async function aptUpdate(): Promise<{ success: boolean; output: string }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/packages/update`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: "{}",
  });
  return (await readJsonOrThrow(r, "apt update")) as { success: boolean; output: string };
}

export async function aptUpgrade(opts: { simulate?: boolean; confirm?: boolean } = {}): Promise<{
  success: boolean;
  simulate: boolean;
  output: string;
}> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/packages/upgrade`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({
      simulate: opts.simulate ?? true,
      confirm: !!opts.confirm,
    }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`apt upgrade: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as { success: boolean; simulate: boolean; output: string };
}

export async function aptInstall(
  packages: string[],
  opts: { confirm?: boolean } = {},
): Promise<{ success: boolean; output: string }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/packages/install`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ packages, confirm: !!opts.confirm }),
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`apt install: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as { success: boolean; output: string };
}

// === Confirm dangerous commands (Сессия: кнопка «Подтвердить» вместо «копировать `подтверждаю xxxx`») ===

export interface ConfirmResult {
  success: boolean;
  command?: string;
  result?: string;
  confirm_id?: string;
  error?: string;
}

/**
 * Подтвердить ожидающую опасную команду по её confirm_id (8-hex).
 * Backend: `POST /api/v1/confirm/{id}` → выполняет команду и возвращает результат.
 */
export async function confirmCommand(confirmId: string): Promise<ConfirmResult> {
  const r = await fetch(
    `${getBaseUrl()}/api/v1/confirm/${encodeURIComponent(confirmId)}`,
    {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: "{}",
    },
  );
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`Подтверждение: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as ConfirmResult;
}

/**
 * Отклонить ожидающую команду без выполнения.
 * Backend: `POST /api/v1/confirm/{id}/reject`.
 */
export async function rejectCommand(confirmId: string): Promise<ConfirmResult> {
  const r = await fetch(
    `${getBaseUrl()}/api/v1/confirm/${encodeURIComponent(confirmId)}/reject`,
    {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: "{}",
    },
  );
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`Отмена: ${r.status} ${t.slice(0, 300)}`);
  }
  return (await r.json()) as ConfirmResult;
}

// === Account: storage / clear-workspace / delete ===

export async function fetchAccountStorage(): Promise<{
  workspace_bytes?: number;
  chats_count?: number;
  files_count?: number;
}> {
  const r = await fetch(`${getBaseUrl()}/api/v1/account/storage`, {
    headers: authHeaders(),
  });
  return (await readJsonOrThrow(r, "Storage недоступен")) as {
    workspace_bytes?: number;
    chats_count?: number;
    files_count?: number;
  };
}

export async function clearWorkspace(): Promise<{ success: boolean }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/account/clear-workspace`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: "{}",
  });
  return (await readJsonOrThrow(r, "Очистка не удалась")) as { success: boolean };
}

// === Vision (P0-3): анализ изображений через vision-LLM ===

export interface VisionProviderInfo {
  provider: string;
  models: string[];
  default_model: string;
  key_configured: boolean;
}

export interface VisionAnalyzeResult {
  description: string;
  model: string;
  provider: string;
  image_size_bytes: number;
  image_mime: string;
  usage?: Record<string, unknown>;
}

export async function fetchVisionProviders(): Promise<VisionProviderInfo[]> {
  const r = await fetch(`${getBaseUrl()}/api/v1/vision/providers`, {
    headers: authHeaders(),
  });
  const data = (await readJsonOrThrow(r, "Vision providers недоступны")) as {
    providers: VisionProviderInfo[];
  };
  return data.providers;
}

export async function analyzeImage(
  file: File,
  opts: { prompt?: string; provider?: string; model?: string } = {},
): Promise<VisionAnalyzeResult> {
  const fd = new FormData();
  fd.append("file", file);
  if (opts.prompt) fd.append("prompt", opts.prompt);
  fd.append("provider", opts.provider || "openai");
  if (opts.model) fd.append("model", opts.model);
  const r = await fetch(`${getBaseUrl()}/api/v1/vision/analyze`, {
    method: "POST",
    headers: authHeaders(), // без Content-Type — браузер сам поставит multipart boundary
    body: fd,
  });
  if (!r.ok) {
    const t = await r.text();
    let detail = t;
    try {
      const j = JSON.parse(t);
      detail = j.detail || t;
    } catch {
      /* keep raw text */
    }
    throw new Error(`Vision: ${r.status} ${detail.slice(0, 300)}`);
  }
  return (await r.json()) as VisionAnalyzeResult;
}

/**
 * Локальное распознавание текста на картинке (rapidocr через
 * /api/v1/files/upload). Не требует сторонних API-ключей и работает offline.
 * Возвращает сырое описание (markdown с распознанным текстом) и file_id.
 */
export async function recognizeImage(file: File): Promise<{
  description: string;
  fileId: string;
}> {
  const fd = new FormData();
  fd.append("files", file);
  const r = await fetch(`${getBaseUrl()}/api/v1/files/upload`, {
    method: "POST",
    headers: authHeaders(),
    body: fd,
  });
  if (!r.ok) {
    const t = await r.text();
    let detail = t;
    try { detail = (JSON.parse(t).detail as string) || t; } catch { /* keep */ }
    throw new Error(`OCR: ${r.status} ${detail.slice(0, 300)}`);
  }
  const data = (await r.json()) as {
    success?: boolean;
    files?: Array<{
      file_id?: string;
      markdown?: string | null;
      markdown_excerpt?: string | null;
      ingest_error?: string | null;
    }>;
  };
  const first = data.files?.[0];
  if (!first?.file_id) throw new Error("OCR: сервер не вернул file_id");
  if (first.ingest_error) throw new Error(`OCR: ${first.ingest_error}`);
  const md = (first.markdown || first.markdown_excerpt || "").trim();
  if (!md) throw new Error("OCR: текст не распознан");
  return { description: md, fileId: first.file_id };
}

// === Streaming chat (SSE) ===

export interface StreamChatOptions {
  message: string;
  sessionId: string;
  reasoning?: "fast" | "deep" | "auto";
  clientContext?: string;
  voice?: boolean;
  mode?: string;
  onEvent: (evt: ChatStreamEvent) => void;
  signal?: AbortSignal;
}

/**
 * SSE-цикл вокруг `POST /api/v1/chat/stream`.
 *
 * Парсит сырой `data: {json}\n` поток и вызывает `onEvent` для каждого события.
 * Не делает retry — это задача вышестоящего слоя (useChat / useSSE хук).
 */
export async function streamChat(opts: StreamChatOptions): Promise<void> {
  const body: Record<string, unknown> = {
    message: opts.message,
    session_id: opts.sessionId,
    reasoning: opts.reasoning ?? "auto",
  };
  if (opts.voice) body.voice = true;
  if (opts.mode) body.mode = opts.mode;
  if (opts.clientContext && opts.clientContext.trim()) {
    body.client_context = opts.clientContext.trim();
  }
  const r = await fetch(
    `${getBaseUrl()}/api/v1/chat/stream`,
    apiFetchInit({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(body),
      signal: opts.signal,
    }),
  );
  if (!r.ok) {
    const t = await r.text();
    throw new Error(`Чат: ${r.status} ${t.slice(0, 400)}`);
  }
  const reader = r.body?.getReader();
  if (!reader) throw new Error("Нет тела ответа");
  await parseSseStream(reader, opts.onEvent);
}

/**
 * Внутренний парсер SSE: строки `data: {json}` → `onEvent(json)`.
 *
 * Также экспортирован для тестов (без сетевого слоя).
 */
export async function parseSseStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onEvent: (evt: ChatStreamEvent) => void,
): Promise<void> {
  const decoder = new TextDecoder();
  let buf = "";
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() || "";
    for (const line of lines) {
      const s = line.trim();
      if (!s.startsWith("data:")) continue;
      const json = s.slice(5).trim();
      if (!json) continue;
      try {
        const obj = JSON.parse(json) as ChatStreamEvent;
        onEvent(obj);
      } catch {
        /* line noise */
      }
    }
  }
}


// === DevOps (9.10) ===

export interface DevopsHealthResponse {
  status: "ok" | "warning" | "critical" | "unknown";
  vps_connected: boolean;
  alerts_count: number;
  message?: string;
  summary?: {
    cpu_usage: number;
    ram_usage: number;
    disk_max_usage: number;
    uptime_seconds: number;
  };
}

export interface DevopsMetricsSnapshot {
  cpu: {
    usage_percent: number;
    idle_percent: number;
    iowait_percent: number;
    load_avg_1m: number;
    load_avg_5m: number;
    load_avg_15m: number;
    n_cpus: number;
  };
  ram: {
    total_mb: number;
    used_mb: number;
    free_mb: number;
    available_mb: number;
    swap_total_mb: number;
    swap_used_mb: number;
    usage_percent: number;
  };
  disks: Array<{
    mount: string;
    filesystem: string;
    total_gb: number;
    used_gb: number;
    avail_gb: number;
    usage_percent: number;
  }>;
  network: {
    tcp_established: number;
    tcp_listen: number;
    tcp_time_wait: number;
    udp_active: number;
  };
  uptime_seconds: number;
  top_processes: Array<{
    pid: number;
    user: string;
    cpu_percent: number;
    mem_percent: number;
    command: string;
  }>;
  errors: string[];
}

export interface DevopsAlert {
  severity: "info" | "warning" | "critical";
  rule: string;
  message: string;
  value: number;
  threshold: number;
}

export async function fetchDevopsHealth(): Promise<DevopsHealthResponse> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/health`, { headers: authHeaders() });
  return (await readJsonOrThrow(r, "DevOps health недоступен")) as DevopsHealthResponse;
}

export async function fetchDevopsMetrics(): Promise<{ snapshot: DevopsMetricsSnapshot }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/metrics`, { headers: authHeaders() });
  return (await readJsonOrThrow(r, "DevOps metrics недоступны")) as { snapshot: DevopsMetricsSnapshot };
}

export async function fetchDevopsAlerts(): Promise<{
  status: string;
  count: number;
  alerts: DevopsAlert[];
}> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/alerts`, { headers: authHeaders() });
  return (await readJsonOrThrow(r, "DevOps alerts недоступны")) as {
    status: string;
    count: number;
    alerts: DevopsAlert[];
  };
}

export interface LogSearchEntry {
  timestamp: string;
  level: string;
  message: string;
  source: string;
  raw: string;
}

export async function searchDevopsLogs(body: {
  query: string;
  sources?: Array<{ kind: string; id?: string }>;
  limit?: number;
  use_semantic?: boolean;
}): Promise<{
  query: string;
  entries: LogSearchEntry[];
  total_matched: number;
  sources_scanned: string[];
  elapsed_ms: number;
  semantic_used: boolean;
}> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/logs/search`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({
      query: body.query,
      sources: body.sources ?? [
        { kind: "systemd_unit", id: "aicores-api" },
        { kind: "nginx_error", id: "" },
      ],
      limit: body.limit ?? 50,
      use_semantic: body.use_semantic ?? false,
    }),
  });
  return (await readJsonOrThrow(r, "Поиск по логам не удался")) as {
    query: string;
    entries: LogSearchEntry[];
    total_matched: number;
    sources_scanned: string[];
    elapsed_ms: number;
    semantic_used: boolean;
  };
}

export async function rollbackDevopsService(body: {
  service: string;
  previous_tag?: string;
  compose_dir?: string;
  host?: string;
}): Promise<{
  success: boolean;
  service: string;
  rolled_back_to: string;
  error: string;
  steps: Array<{ name: string; ok: boolean; output: string }>;
}> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/rollback`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return (await readJsonOrThrow(r, "Rollback не удался")) as {
    success: boolean;
    service: string;
    rolled_back_to: string;
    error: string;
    steps: Array<{ name: string; ok: boolean; output: string }>;
  };
}


// === DevOps Native (P3 wave-1): audit log + settings + terminal ==============

export interface DevopsAuditEntry {
  id: string;
  ts: string | null;
  user_id: string | null;
  user_email: string | null;
  method: string;
  path: string;
  status_code: number;
  duration_ms: number;
  confirm: boolean;
  args_summary: string | null;
  ip: string | null;
  user_agent: string | null;
  error: string | null;
}

export async function fetchDevopsAudit(opts: {
  limit?: number;
  offset?: number;
  user?: string;
  path?: string;
  method?: string;
  onlyDestructive?: boolean;
} = {}): Promise<{
  items: DevopsAuditEntry[];
  total: number;
  offset: number;
  limit: number;
}> {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.offset !== undefined) params.set("offset", String(opts.offset));
  if (opts.user) params.set("user", opts.user);
  if (opts.path) params.set("path", opts.path);
  if (opts.method) params.set("method", opts.method);
  if (opts.onlyDestructive) params.set("only_destructive", "true");
  const r = await fetch(
    `${getBaseUrl()}/api/v1/devops/audit?${params.toString()}`,
    { headers: authHeaders() },
  );
  return (await readJsonOrThrow(r, "Audit log недоступен")) as {
    items: DevopsAuditEntry[];
    total: number;
    offset: number;
    limit: number;
  };
}

export interface DevopsAuditStats {
  since: string;
  hours: number;
  total: number;
  confirms: number;
  errors: number;
  top_paths: Array<{ path: string; count: number }>;
  top_users: Array<{ email: string | null; count: number }>;
}

export async function fetchDevopsAuditStats(hours: number = 24): Promise<DevopsAuditStats> {
  const r = await fetch(
    `${getBaseUrl()}/api/v1/devops/audit/stats?hours=${hours}`,
    { headers: authHeaders() },
  );
  return (await readJsonOrThrow(r, "Audit stats недоступны")) as DevopsAuditStats;
}

export interface DevopsSettings {
  allowed_dangerous_ops: string[];
  audit_retention_days: number;
  terminal_enabled: boolean;
}

export interface KnownDangerousOp {
  key: string;
  label: string;
}

export async function fetchDevopsSettings(): Promise<{
  settings: DevopsSettings;
  known_dangerous_ops: KnownDangerousOp[];
}> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/settings`, {
    headers: authHeaders(),
  });
  return (await readJsonOrThrow(r, "Настройки DevOps недоступны")) as {
    settings: DevopsSettings;
    known_dangerous_ops: KnownDangerousOp[];
  };
}

export async function updateDevopsSettings(
  patch: Partial<DevopsSettings>,
): Promise<{ settings: DevopsSettings }> {
  const r = await fetch(`${getBaseUrl()}/api/v1/devops/settings`, {
    method: "PUT",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  return (await readJsonOrThrow(r, "Сохранение настроек не удалось")) as {
    settings: DevopsSettings;
  };
}

/**
 * Построить URL WebSocket для live-терминала.
 * Бэк ждёт JWT в query (?token=...).
 */
export function buildDevopsTerminalUrl(): string {
  const base = getBaseUrl().replace(/\/$/, "");
  const wsBase = base.replace(/^http:/, "ws:").replace(/^https:/, "wss:");
  const token = getToken() || "";
  return `${wsBase}/api/v1/devops/terminal?token=${encodeURIComponent(token)}`;
}
